import { type ChangeEvent, type FormEvent, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { action } from "mobx";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Button, Box, TextField, Typography } from "@mui/material";
import { routes } from "@/router/Routes";
import { passwordHelperText } from "@/util/commonStrings";
import { interactionStore, authStore, snackStore } from "@/stores/Stores";
import { Page } from "@/components/Page";

export const Signup = observer(() => {
  const { t } = useTranslation();

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [emailTouched, setEmailTouched] = useState(false);

  // Password managers can fill the inputs without firing change events, so read the inputs on submit.
  const syncFromInputs = action(() => {
    authStore.unknownUser.email = emailInputRef.current?.value ?? "";
    authStore.unknownUser.password = passwordInputRef.current?.value ?? "";
  });

  const onSubmitHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    syncFromInputs();
    setEmailTouched(true);

    if (!authStore.canSignup || interactionStore.loading) {
      return;
    }

    await authStore.signup();
    if (authStore.expectingRegistrationCode) {
      snackStore.showSuccess(`${t("Please check your email and verify your email address")}.`);
    }
  };

  const showEmailError = emailTouched && !authStore.unknownUser.isEmailValid;

  return (
    <Page title="Register" showLanguages>
      <form onSubmit={onSubmitHandler} noValidate>
        <TextField
          inputRef={emailInputRef}
          onChange={action((event: ChangeEvent<HTMLInputElement>) => {
            authStore.unknownUser.email = event.target.value;
          })}
          onBlur={() => setEmailTouched(true)}
          fullWidth
          name="email"
          type="email"
          label={t("Email address")}
          variant="outlined"
          size="medium"
          error={showEmailError}
          helperText={showEmailError ? "Invalid email address" : ""}
          autoComplete="username"
        />

        <TextField
          inputRef={passwordInputRef}
          onChange={action((event: ChangeEvent<HTMLInputElement>) => {
            authStore.unknownUser.password = event.target.value;
          })}
          fullWidth
          name="password"
          type="password"
          label={t("Password")}
          variant="outlined"
          size="medium"
          error={authStore.unknownUser.password !== undefined && !authStore.unknownUser.isPasswordValid}
          helperText={authStore.unknownUser.password && authStore.unknownUser.isPasswordValid ? "" : passwordHelperText}
          autoComplete="new-password"
        />

        <Button
          disabled={authStore.expectingRegistrationCode || interactionStore.loading}
          fullWidth
          type="submit"
          variant="contained"
          color="primary"
          sx={{ marginTop: "1rem" }}
        >
          {t("Sign up")}
        </Button>
      </form>

      <Box mt="1rem">
        <Typography variant="body2">
          {t("Already a user?")} <Link to={routes.pages.login}>{t("Log in")}</Link>
        </Typography>
      </Box>
    </Page>
  );
});
